<template>
  <div class="timeline-container">
    <table class="schedule-table">
      <thead>
        <tr>
          <th class="fixed-column slots-header"></th>
          <th
            v-for="(weekInfo, index) in weekHeaders"
            :key="'week-' + index"
            :colspan="7"
            class="week-header text-center"
          >
            Semana {{ weekInfo.weekNumber }} <span>{{ weekInfo.month }}</span>
            {{ weekInfo.year }}
            <span
              v-if="weekInfo.weekNumber == 1 && weekInfo.month == 'Diciembre'"
              >- Enero {{ weekInfo.year + 1 }}</span
            >
          </th>
        </tr>
        <tr>
          <th class="fixed-column slots-header"></th>
          <th
            v-for="day in days"
            :key="day.toString()"
            class="day-header text-center"
            v-html="formatDayHeader(day)"
          ></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(slot, index) in timeSlots" :key="slot.id">
          <tr>
            <td
              class="fixed-column slots-column"
              :style="{ backgroundColor: slot.color }"
            >
              {{ slot.name }}
            </td>
            <!-- :class="{ 'bg-grey-lighten-4': hover }" -->
            <td
              v-for="day in days"
              :key="day.toString()"
              class="time-slot cursor-pointer"
              :style="{
                backgroundColor: shouldApplyColor(slot) ? slot.color : 'white'
              }"
            >
              <div class="d-flex flex-wrap gap-1 slot-content">
                <template v-if="getAssignments(day, slot.id).length > 0">
                  <v-chip
                    v-for="user in getAssignments(day, slot.id)"
                    :key="user.id"
                    :style="{
                      backgroundColor: getColorUser(user.id),
                      color: isColorDark(getColorUser(user.id))
                        ? 'white'
                        : 'black',
                      border: user.prevision ? '3px solid red' : 'none'
                    }"
                    size="x-small"
                    variant="elevated"
                    class="cursor-pointer"
                    density="compact"
                  >
                    {{ user.iniciales }}
                  </v-chip>
                </template>
              </div>
            </td>
          </tr>
          <!-- v-if="isTurnoTarde(slot) && index < timeSlots.length -   1" -->
          <tr
            v-if="
              (index == 3 && props.puestoId == 1) ||
              (index == 6 && props.puestoId == 2)
            "
            class="separator-row"
          >
            <td class="fixed-column"></td>
            <td
              v-for="day in days"
              :key="'sep-' + day.toString()"
              class="separator-cell"
            ></td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useTurnosStore } from '@/stores/turnosStore'
import { isColorDark } from '@/utils/helpers'

const turnosStore = useTurnosStore()
const { months } = turnosStore // Desestructuramos months del store

const props = defineProps({
  weekOffset: {
    type: Number,
    required: true
  },
  selectedYear: {
    type: Number,
    required: true
  },
  timeSlots: {
    type: Array,
    required: true
  },
  schedules: {
    type: Object,
    required: true
  },
  users: {
    type: Array,
    required: true
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  puestoId: {
    type: Number,
    required: true
  }
})

defineEmits(['slotClick', 'chipClick'])

const dayNames = ['DOM', 'LUN', 'MAR', 'MIE', 'JUE', 'VIE', 'SAB']

const formatDayHeader = (date) => {
  return `${dayNames[date.getDay()]}<br>${date.getDate()}`
}

const getDateKey = (date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

const getAssignments = (day, slotId) => {
  const dayStr = getDateKey(day)
  //console.log('Buscando asignaciones para:', dayStr, slotId)
  //console.log('Schedules disponibles:', props.schedules)

  // Si no hay datos para ese día, retornar array vacío
  if (!props.schedules[dayStr]) {
    //console.log('No hay datos para el día:', dayStr)
    return []
  }

  // Retornar los usuarios asignados a ese turno específico
  const assignments = props.schedules[dayStr][slotId] || []
  //console.log('Asignaciones encontradas:', assignments)
  return assignments
}

const getColorUser = (userId) => {
  const user = props.users.find((u) => u.id === userId)
  return user?.color || '#CCCCCC'
}

function getMondayOfWeek(weekNumber, year) {
  console.log('getMondayOfWeek', weekNumber, year)
  // Asegurarnos de que weekNumber sea un número válido
  if (weekNumber < 1 || weekNumber > 53) {
    throw new Error('El número de semana debe estar entre 1 y 53.')
  }

  // Crear una fecha al inicio del año
  const firstDayOfYear = new Date(year, 0, 1)

  // Calcular el día de la semana del 1 de enero (0 = domingo, 1 = lunes, ..., 6 = sábado)
  const firstDayOfWeek = firstDayOfYear.getDay()

  // Encontrar el jueves de la primera semana del año
  const daysToFirstThursday = (11 - firstDayOfWeek) % 7 // Ajuste para alcanzar el jueves
  const firstThursday = new Date(year, 0, 1 + daysToFirstThursday)

  // Calcular el lunes de la semana 1 basado en el primer jueves
  const firstMonday = new Date(firstThursday)
  firstMonday.setDate(firstThursday.getDate() - 3) // Retroceder al lunes de esa semana

  // Calcular el lunes de la semana deseada
  const targetMonday = new Date(firstMonday)
  targetMonday.setDate(firstMonday.getDate() + (weekNumber - 1) * 7)

  return targetMonday
}

const days = computed(() => {
  if (props.weekOffset === undefined) return []

  try {
    const firstDay = getMondayOfWeek(props.weekOffset, props.selectedYear)
    const result = []

    for (let i = 0; i < 42; i++) {
      const day = new Date(firstDay)
      day.setDate(firstDay.getDate() + i)
      result.push(day)
    }

    return result
  } catch (error) {
    console.error('Error calculando días:', error)
    return []
  }
})

const weekHeaders = computed(() => {
  if (!days.value.length) return []

  const headers = []
  for (let i = 0; i < 6; i++) {
    const weekStart = days.value[i * 7]
    const weekNumber = getISOWeekNumber(weekStart)
    const month = months[weekStart.getMonth()]
    const year = weekStart.getFullYear()

    headers.push({
      weekNumber,
      month,
      year
    })
  }

  return headers
})

// Mantenemos getISOWeekNumber para los headers
const getISOWeekNumber = (date) => {
  const target = new Date(date.valueOf())
  const dayNr = (date.getDay() + 6) % 7

  target.setDate(target.getDate() - dayNr + 3)

  const firstThursday = target.valueOf()
  target.setMonth(0, 1)

  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7))
  }

  return 1 + Math.ceil((firstThursday - target) / 604800000)
}

// // Función para identificar si un slot es un turno tarde
// const isTurnoTarde = (slot) => {
//   return slot.name.toLowerCase().includes('tarde')
// }

// Función para determinar si se debe aplicar el color de fondo
const shouldApplyColor = (slot) => {
  const name = slot.name.toLowerCase()
  return !(
    name.includes('mañana') ||
    name.includes('tarde') ||
    name.includes('noche')
  )
}
</script>

<style scoped>
.timeline-container {
  position: relative;
  overflow-x: auto;
  margin: 0 -16px;
}

.schedule-table {
  /* width: 100% !important; */
  /* min-width: 100%; */
  border-collapse: collapse;
  table-layout: fixed;
  background: white;
  border: 2px solid #aaa;
}

.fixed-column {
  position: sticky;
  padding: 4px !important;
  width: 82px !important;
  font-size: 12px !important;
  text-align: center;
  left: 0;
  z-index: 1;
  border: 1px solid #e0e0e0;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
}

.slots-header {
  width: fit-content;
  min-width: auto;
  padding: 6px 12px;
  white-space: nowrap;
}

.slots-column {
  padding: 4px 6px;
  white-space: nowrap;
}

.week-header {
  border: 1px solid #e0e0e0;
  border-right: 2px solid #e0e0e0;
  padding: 8px;
  font-weight: bold;
  white-space: nowrap;
}

.day-header {
  /* padding: 6px; */
  border: 1px solid #e0e0e0;
  font-size: 0.9rem;
  font-weight: bold;
  white-space: nowrap;
  width: 45px !important;
}

.time-slot {
  border: 1px solid #e0e0e0;
  /* padding: 4px; */
  vertical-align: top;
  width: 45px !important;
  height: auto !important;
  min-height: 30px;
}

.slot-content {
  min-height: unset;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}

.border-primary {
  border: 2px solid rgb(var(--v-theme-primary));
}

.gap-1 {
  gap: 4px;
}

.text-center {
  text-align: center;
}

/* Ajustar el tamaño del chip */
:deep(.v-chip) {
  height: 20px !important;
  padding: 0 6px !important;
  width: 90%;
  justify-content: center;
  margin: 1px 0;
}

/* Asegurar que la tabla mantiene su estructura */
tbody tr {
  height: auto !important;
}

/* Estilo para el scrollbar */
.slot-content::-webkit-scrollbar {
  width: 4px;
}

.slot-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.slot-content::-webkit-scrollbar-thumb {
  background: #aaa;
  border-radius: 2px;
}

.slot-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Añadir borde especial solo para el borde derecho de los domingos */
.schedule-table tbody tr td:nth-child(7n + 1) {
  border-right: 2px solid #aaa;
}

/* Estilo para la fila separadora */
.separator-row {
  height: 3px !important;
}

.separator-cell {
  padding: 0 !important;
  height: 3px !important;
  background-color: black !important;
  border: none !important;
}
</style>
